import api from './config';

export interface CrawlLog {
  id: number;
  crawl_type: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  triggered_by: string;
  started_at: string;
  completed_at: string | null;
  total_outlets: number;
  successful_outlets: number;
  failed_outlets: number;
  total_reviews: number;
  new_reviews: number;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface OutletCrawlStatus {
  id: number;
  brand: string;
  outlet: string;
  alamat: string;
  url: string;
  status: string;
  outlet_code: string;
  updated_at: string;
}

export interface FileLogEntry {
  filename: string;
  size: number;
  modified_at: string;
}

export interface FileLogContent {
  filename: string;
  total_lines: number;
  lines: string[];
}

export interface StatusSummary {
  status: string;
  count: string;
}

interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export type CrawlType = 'google_reviews' | 'instagram' | 'tiktok' | 'csv_google_reviews' | 'all';

interface RecrawlOutletParams {
  outlet_id: number;
  start_date?: string;
  end_date?: string;
}

export const crawlManagementService = {
  async getLogs(params: {
    page?: number;
    limit?: number;
    crawl_type?: string;
    status?: string;
  } = {}): Promise<PaginatedResponse<CrawlLog>> {
    const response = await api.get('/admin/crawl/logs', { params });
    return response.data;
  },

  async getLog(id: number): Promise<{ success: boolean; data: CrawlLog }> {
    const response = await api.get(`/admin/crawl/logs/${id}`);
    return response.data;
  },

  async getFileLogs(): Promise<{ success: boolean; data: FileLogEntry[] }> {
    const response = await api.get('/admin/crawl/file-logs');
    return response.data;
  },

  async getFileLogContent(filename: string, tail?: number): Promise<{ success: boolean; data: FileLogContent }> {
    const response = await api.get(`/admin/crawl/file-logs/${filename}`, { params: { tail } });
    return response.data;
  },

  async getOutletStatus(params: {
    page?: number;
    limit?: number;
    status?: string;
    brand?: string;
  } = {}): Promise<PaginatedResponse<OutletCrawlStatus> & { summary: StatusSummary[] }> {
    const response = await api.get('/admin/crawl/outlet-status', { params });
    return response.data;
  },

  async triggerCrawl(crawl_type: CrawlType): Promise<{ success: boolean; data: { log_id: number; crawl_type: string; status: string }; message: string }> {
    const response = await api.post('/admin/crawl/trigger', { crawl_type });
    return response.data;
  },

  async recrawlOutlet(params: RecrawlOutletParams): Promise<{ success: boolean; data: { log_id: number; outlet_id: number; start_date: string | null; end_date: string | null; status: string }; message: string }> {
    const response = await api.post('/admin/crawl/recrawl-outlet', params);
    return response.data;
  },

  async getActiveCrawls(): Promise<{ success: boolean; data: { active_processes: Record<string, boolean>; running_logs: CrawlLog[] } }> {
    const response = await api.get('/admin/crawl/active');
    return response.data;
  },
};
